"use client"

import React, { useState } from "react"
import Link from "next/link"
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { A9entLogo } from "@/components/a9ent-logo"
import { usePublicAuthI18n } from "@/components/public-auth/i18n/public-auth-i18n-provider"
import { appendLngParam } from "@/lib/public-auth/append-lng-param"

export function ForgotPasswordForm() {
  const { t, locale } = usePublicAuthI18n()
  const [email, setEmail] = useState("")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL_NEW || "http://localhost:3001"

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setIsLoading(true)

    try {
      const res = await fetch(`${apiBaseUrl}/api/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      })

      if (!res.ok) {
        try {
          const data = await res.json()
          setError(data?.error || data?.message || t("common.requestFailed"))
        } catch {
          setError(t("common.requestFailed"))
        }
        return
      }

      setIsSubmitted(true)
    } catch {
      setError(t("common.networkError"))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-muted/30">
      <header className="border-b bg-card">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
          <Link href="/" className="flex items-center">
            <A9entLogo height={28} className="h-5 w-auto" />
          </Link>
          <Link href={appendLngParam("/login", locale)}>
            <Button variant="ghost" size="sm">
              {t("common.signIn")}
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="rounded-2xl border bg-card p-8 shadow-sm">
            {!isSubmitted ? (
              <>
                <div className="mb-6 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground">{t("forgotPassword.title")}</h1>
                  <p className="mt-1 text-sm text-muted-foreground">{t("forgotPassword.subtitle")}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">{t("common.email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t("signup.emailPlaceholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("forgotPassword.sending")}
                      </>
                    ) : (
                      t("forgotPassword.resetPassword")
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    href={appendLngParam("/login", locale)}
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t("forgotPassword.backToSignIn")}
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">{t("forgotPassword.checkEmailTitle")}</h1>
                <p className="mt-2 text-sm text-muted-foreground">{t("forgotPassword.checkEmailBody")}</p>
                <p className="mt-1 font-medium text-foreground">{email}</p>
                <p className="mt-3 text-xs text-muted-foreground">{t("forgotPassword.googleHint")}</p>

                <p className="mt-6 text-sm text-muted-foreground">
                  {t("forgotPassword.resendPrompt")}{" "}
                  <button
                    type="button"
                    onClick={() => setIsSubmitted(false)}
                    className="font-medium text-primary hover:underline"
                  >
                    {t("forgotPassword.resendAction")}
                  </button>
                </p>

                <div className="mt-6">
                  <Link
                    href={appendLngParam("/login", locale)}
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t("forgotPassword.backToSignIn")}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
